import { Box, Flex, IconButton, Stack, useDisclosure } from '@chakra-ui/react';
import { inToCm, kgToLb, prettyNumber } from '@pelicargo/utils';
import { UseFormReturn } from 'react-hook-form';
import { HiTrash } from 'react-icons/hi';

import { useDeleteCargo } from '../../hooks/cargo/useDelete';
import { CargoOutput, useCargoForm } from '../../hooks/cargo/useForm';
import { useUpdateCargo } from '../../hooks/cargo/useUpdate';
import { AlertModal } from '../AlertModal/AlertModal';
import { SimpleForm } from '../SimpleForm';
import { SimpleHandlingOptions } from '../SimpleHandlingOptions';
import { SimpleInput } from '../SimpleInput';
import { SimpleLabeledPopoverInput } from '../SimpleLabeledPopoverInput';
import { SimplePopoverUnifiedInput } from '../SimpleUnifiedPopoverInput';

type Props = {
  cargo: CargoOutput;
};

const formatWeight = (weight: any) => {
  if (weight === null || weight === undefined) return '';
  return prettyNumber(weight);
};

export const CargoListItem = ({ cargo }: Props) => {
  const { methods } = useCargoForm(cargo);
  const [updateCargo] = useUpdateCargo(cargo?.id);
  const [deleteCargo, isDeleting] = useDeleteCargo(cargo.id);

  const deleteDisclosure = useDisclosure();

  const handleCargoDelete = async () => {
    if (!cargo.id) return;
    await deleteCargo(cargo.id);
    deleteDisclosure.onClose();
  };

  // Red flag detectors 🚩
  const LENGTH_FLAG_THRESHOLD_INCHES = 120;
  const WIDTH_FLAG_THRESHOLD_INCHES = 96; // about 244 cm
  const HEIGHT_FLAG_THRESHOLD_INCHES = 62;
  const lengthFlagged = cargo?.length > LENGTH_FLAG_THRESHOLD_INCHES;
  const widthFlagged = cargo?.width >= WIDTH_FLAG_THRESHOLD_INCHES;
  const heightFlagged = cargo?.height > HEIGHT_FLAG_THRESHOLD_INCHES;
  const dimensionsFlagged = lengthFlagged || widthFlagged || heightFlagged;
  const stackableFlagged = !cargo?.handling?.includes('Stackable');
  const handlingFlagged = stackableFlagged;

  return (
    <SimpleForm onValid={updateCargo} {...(methods as UseFormReturn<any>)}>
      <Box position="relative" w="full" className="cargo-item">
        <Flex direction="row" w="full">
          <Stack direction="row" flex={1} align="center">
            <SimplePopoverUnifiedInput
              flex={2}
              label="Dimensions"
              names={['length', 'width', 'height']}
              joiner="x"
              isHighlighted={dimensionsFlagged}
              tabIndex={0}
              title={`${Math.round(inToCm(cargo?.length ?? 0))} x ${Math.round(inToCm(cargo?.width ?? 0))} x ${Math.round(inToCm(cargo?.height ?? 0))} cm`}
            >
              <Stack direction="column" spacing={2}>
                <SimpleInput name="length" label="Length" ignoreOnBlur min={0} isHighlighted={lengthFlagged} />
                <SimpleInput name="width" label="Width" ignoreOnBlur min={0} isHighlighted={widthFlagged} />
                <SimpleInput name="height" label="Height" ignoreOnBlur min={0} isHighlighted={heightFlagged} />
              </Stack>
            </SimplePopoverUnifiedInput>
            <SimpleLabeledPopoverInput
              flex={1}
              name="weight"
              label="Weight"
              inputType="input"
              formatDisplayValue={formatWeight}
              tabIndex={0}
              title={`${Math.round(kgToLb(cargo?.weight ?? 0))} lb`}
            />
            <SimpleLabeledPopoverInput
              flex={1}
              name="quantity"
              label="Qty"
              inputType="input"
              numberOptions={{ inputMode: 'numeric' }}
              tabIndex={0}
            />
            <SimpleHandlingOptions
              handlingName="handling"
              packingName="packing"
              label="Handling"
              flex={2}
              isHighlighted={handlingFlagged}
              tabIndex={0}
            />
            <IconButton
              aria-label="Delete"
              isLoading={isDeleting}
              variant="ghost"
              colorScheme="red"
              size="xs"
              position="absolute"
              right={1}
              top="1px"
              icon={<HiTrash />}
              opacity={0}
              onClick={deleteDisclosure.onOpen}
              sx={{ '.cargo-item:hover &': { opacity: 1 } }}
            />
          </Stack>

          <AlertModal
            {...deleteDisclosure}
            title="Delete Cargo"
            message="Are you sure?"
            btnCancel="Cancel"
            btnDelete="Delete"
            handleConfirm={handleCargoDelete}
            isDeleting={isDeleting}
          />
        </Flex>
      </Box>
    </SimpleForm>
  );
};
